<template>
  <div>
    <page-header
      :title="title"
      :container-class="containerClass"
      info-text="These FAQs serve as a template that can be used to create program-specific FAQs."
    />

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Search -->
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent>
                  <b-form-input class="w-lg-auto" placeholder="Search FAQs" v-model="searchTerm" @input="onSearch" />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>
              <div class="col-lg d-flex justify-content-end mt-2 mt-lg-0">
                <b-btn variant="primary" :to="{ name: 'program-faqs-add' }">
                  Add FAQ
                </b-btn>
              </div>
            </div>
          </div>

          <b-table
            :fields="tableColumns"
            :items="paginatedFaqs"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
          >
            <template #cell(menu)="data">
              <div class="text-center">
                <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted" no-caret>
                  <template #button-content>
                    <i class="material-icons icon-24pt card-course__icon-favorite text-info">more_vert</i>
                    <span class="sr-only"></span>
                  </template>

                  <b-dropdown-item :to="{ name: 'program-faqs-edit', params: { id: data.item.id } }">
                    <md-icon class="text-info">edit</md-icon> Edit FAQ
                  </b-dropdown-item>

                  <b-dropdown-item @click.prevent="deleteFaq(data.item)">
                    <md-icon class="text-danger">delete</md-icon> Delete FAQ
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>

            <template #cell(question)="data">
              <div class="d-flex align-items-center w-100">
                <span class="mr-2">{{ data.item.question }}</span>
                <b-badge v-if="data.item.is_default" variant="primary">Default</b-badge>
              </div>
            </template>

            <template #cell(answer)="data">
              <span class="d-inline-block w-100">{{ data.item.answer }}</span>
            </template>
          </b-table>

          <div class="card-footer" v-if="totalFaqs > perPage">
            <pagination
              v-model="currentPage"
              :total-rows="totalFaqs"
              :per-page="perPage"
              aria-controls="program-faqs-table"
              @change="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '@/common/constants';

export default {
  name: 'ProgramFaqsTable',
  components: { PageHeader, Pagination, MdIcon },
  extends: Page,
  data() {
    return {
      title: 'Program FAQs',
      isLoading: false,
      searchTerm: '',
      allFaqs: [],
      currentPage: 1,
      perPage: DEFAULT_PAGE_SIZE,
    };
  },
  computed: {
    // breadcrumb() {
    //   return [
    //     { text: this.$t('home'), to: this.routes.home },
    //     { text: 'Program FAQs', active: true },
    //   ];
    // },
    sortedFaqs() {
      return [...this.allFaqs].sort((a, b) => (a.order_num || 0) - (b.order_num || 0));
    },
    filteredFaqs() {
      if (!this.searchTerm) {
        return this.sortedFaqs;
      }
      const term = this.searchTerm.toLowerCase();
      return this.sortedFaqs.filter(faq => {
        return (faq.question || '').toLowerCase().includes(term) || (faq.answer || '').toLowerCase().includes(term);
      });
    },
    paginatedFaqs() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredFaqs.slice(start, start + this.perPage);
    },
    totalFaqs() {
      return this.filteredFaqs.length;
    },
    tableColumns() {
      return [
        {
          key: 'menu',
          label: '',
          tdClass: 'text-left',
        },
        {
          key: 'question',
          label: 'Question',
          thStyle: { width: '45%' },
          tdClass: 'faq-question-column align-middle',
        },
        {
          key: 'answer',
          label: 'Answer',
          thStyle: { width: '45%' },
          tdClass: 'faq-answer-column align-middle',
        },
      ];
    },
  },
  methods: {
    ...mapActions('program', ['getDefaultProgramFaqs', 'deleteDefaultProgramFaq']),
    async fetchFaqs() {
      this.isLoading = true;
      try {
        const response = await this.getDefaultProgramFaqs({});
        this.allFaqs = response.data || [];
        this.currentPage = 1;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      } finally {
        this.isLoading = false;
      }
    },
    onSearch() {
      this.debouncedFilter(this);
    },
    debouncedFilter: debounce(vm => {
      vm.currentPage = 1;
    }, 300),
    onPageChange(page) {
      this.currentPage = page;
      document.getElementById('app').scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    editFaq(faq) {
      this.$router.push({
        name: 'program-faqs-edit',
        params: { id: faq.id },
      });
    },
    async deleteFaq(faq) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete this FAQ: "${faq.question}"?`,
        {
          title: 'Confirm Deletion',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        }
      );

      if (confirmed) {
        this.isLoading = true;
        try {
          await this.deleteDefaultProgramFaq(faq.id);
          this.makeToast({ variant: 'success', msg: 'FAQ deleted successfully!' });
          await this.fetchFaqs();
        } catch (error) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
  async mounted() {
    this.fetchFaqs();
  },
};
</script>
